import React, { useEffect, useMemo, useRef, useState } from 'react';
import ScrollToTop from 'react-scroll-up';
import { FiAward, FiChevronDown, FiChevronUp, FiClock, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import Helmet from '../../component/common/Helmet';
import {
    OSCARS_2026_CATEGORIES,
    OSCARS_2026_CATEGORY_COUNT,
    OSCARS_2026_LOCK_AT,
    OSCARS_2026_USERS,
} from '../../data/oscars2026MatteoJulia';
import {
    fetchOscars2026Results,
    fetchOscars2026Picks,
    saveOscars2026Pick,
    saveOscars2026Result,
} from '../../lib/oscars2026MatteoJuliaSupabase';
import './oscars2026.css';

const LOCK_DATE = new Date(OSCARS_2026_LOCK_AT);
const ET_LOCK_LABEL = 'March 15, 2026 at 7:00 p.m. ET';
const ACCESS_PASSWORD = 'thesecretagent!';
const ACCESS_STORAGE_KEY = 'oscars2026-matteo-julia-access-granted';
const COMPLETION_MODAL_KEY_PREFIX = 'oscars2026-matteo-julia-complete-modal-shown';
const USER_STORAGE_KEY = 'oscars2026-matteo-julia-selected-user';

const createEmptyState = () =>
    OSCARS_2026_USERS.reduce((accumulator, user) => {
        accumulator[user.slug] = {};
        return accumulator;
    }, {});

const getCountdownParts = (distanceMs) => {
    const safeDistance = Math.max(distanceMs, 0);
    const totalSeconds = Math.floor(safeDistance / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [
        { label: 'Days', value: days },
        { label: 'Hours', value: hours },
        { label: 'Minutes', value: minutes },
        { label: 'Seconds', value: seconds },
    ];
};

const formatCountdownValue = (value) => String(value).padStart(2, '0');

const Oscars2026MatteoJulia = () => {
    const [selectedUser, setSelectedUser] = useState('matteo');
    const [picks, setPicks] = useState(createEmptyState);
    const [results, setResults] = useState({});
    const [countdown, setCountdown] = useState(() => getCountdownParts(LOCK_DATE.getTime() - Date.now()));
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [accessPassword, setAccessPassword] = useState('');
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [authError, setAuthError] = useState('');
    const [isChoosingUser, setIsChoosingUser] = useState(false);
    const [isComparisonOpen, setIsComparisonOpen] = useState(false);
    const [showFloatingNav, setShowFloatingNav] = useState(false);
    const [showCelebrationModal, setShowCelebrationModal] = useState(false);
    const comparisonRef = useRef(null);

    const isLocked = Date.now() >= LOCK_DATE.getTime();

    const loadPageData = async (showRefreshMessage = false) => {
        try {
            setErrorMessage('');
            if (showRefreshMessage) {
                setStatusMessage('Refreshing picks...');
            }

            const [rows, winnerRows] = await Promise.all([
                fetchOscars2026Picks(),
                fetchOscars2026Results(),
            ]);
            const nextState = createEmptyState();
            const nextResults = {};

            rows.forEach((row) => {
                if (!nextState[row.user_slug]) {
                    return;
                }

                nextState[row.user_slug][row.category_slug] = row.nominee_slug;
            });

            winnerRows.forEach((row) => {
                nextResults[row.category_slug] = row.nominee_slug;
            });

            setPicks(nextState);
            setResults(nextResults);
            if (showRefreshMessage) {
                setStatusMessage('Latest movies and categories loaded.');
            }
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const hasAccess = window.localStorage.getItem(ACCESS_STORAGE_KEY) === 'true';
        const storedUser = window.localStorage.getItem(USER_STORAGE_KEY);

        setIsAuthorized(hasAccess);
        if (hasAccess && storedUser && OSCARS_2026_USERS.some((user) => user.slug === storedUser)) {
            setSelectedUser(storedUser);
            setIsChoosingUser(false);
            setIsLoading(true);
            loadPageData();
        } else if (hasAccess) {
            setIsChoosingUser(true);
        }
        else {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = window.setInterval(() => {
            setCountdown(getCountdownParts(LOCK_DATE.getTime() - Date.now()));
        }, 1000);

        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!isAuthorized || isChoosingUser) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            loadPageData();
        }, 15000);

        return () => window.clearInterval(intervalId);
    }, [isAuthorized, isChoosingUser]);

    useEffect(() => {
        if (!isAuthorized || isChoosingUser) {
            return undefined;
        }

        const handleScroll = () => {
            setShowFloatingNav(window.scrollY > 260);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, [isAuthorized, isChoosingUser]);

    useEffect(() => {
        if (!statusMessage) {
            return undefined;
        }

        const timeout = window.setTimeout(() => {
            setStatusMessage('');
        }, 2400);

        return () => window.clearTimeout(timeout);
    }, [statusMessage]);

    const handleAccessSubmit = (event) => {
        event.preventDefault();

        if (accessPassword !== ACCESS_PASSWORD) {
            setAuthError('Wrong password.');
            return;
        }

        window.localStorage.setItem(ACCESS_STORAGE_KEY, 'true');
        setIsAuthorized(true);
        setAuthError('');
        setIsChoosingUser(true);
    };

    const handleUserSelection = (userSlug) => {
        window.localStorage.setItem(USER_STORAGE_KEY, userSlug);
        setSelectedUser(userSlug);
        setIsChoosingUser(false);
        setIsLoading(true);
        loadPageData();
    };

    const completionByUser = useMemo(
        () =>
            OSCARS_2026_USERS.reduce((accumulator, user) => {
                accumulator[user.slug] = Object.values(picks[user.slug] || {}).filter(Boolean).length;
                return accumulator;
            }, {}),
        [picks]
    );

    const everyoneFinished = OSCARS_2026_USERS.every(
        (user) => completionByUser[user.slug] === OSCARS_2026_CATEGORY_COUNT
    );
    const selectedUserFinished = completionByUser[selectedUser] === OSCARS_2026_CATEGORY_COUNT;

    const otherUser = OSCARS_2026_USERS.find((user) => user.slug !== selectedUser);

    useEffect(() => {
        if (selectedUserFinished) {
            setIsComparisonOpen(true);
        }
    }, [selectedUserFinished]);

    useEffect(() => {
        if (!selectedUser || !selectedUserFinished || isChoosingUser) {
            return;
        }

        const storageKey = `${COMPLETION_MODAL_KEY_PREFIX}-${selectedUser}`;
        const alreadyShown = window.localStorage.getItem(storageKey) === 'true';

        if (!alreadyShown) {
            window.localStorage.setItem(storageKey, 'true');
            setShowCelebrationModal(true);
        }
    }, [selectedUser, selectedUserFinished, isChoosingUser]);

    const scrollToComparison = () => {
        if (!comparisonRef.current) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const top = comparisonRef.current.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    const handlePick = async (category, nomineeSlug) => {
        if (isLocked || isSaving) {
            return;
        }

        const previousValue = picks[selectedUser]?.[category.slug];
        const nextState = {
            ...picks,
            [selectedUser]: {
                ...picks[selectedUser],
                [category.slug]: nomineeSlug,
            },
        };

        setPicks(nextState);
        setIsSaving(true);
        setErrorMessage('');
        setStatusMessage('Saving...');

        try {
            await saveOscars2026Pick({
                user_slug: selectedUser,
                user_name: selectedUser === 'matteo' ? 'Matteo' : 'Julia',
                category_slug: category.slug,
                category_name: category.name,
                category_order: OSCARS_2026_CATEGORIES.findIndex((item) => item.slug === category.slug),
                nominee_slug: nomineeSlug,
                updated_at: new Date().toISOString(),
            });

            setStatusMessage('Pick saved.');
            await loadPageData();
        } catch (error) {
            setPicks((currentState) => {
                const currentUserPicks = { ...currentState[selectedUser] };

                if (previousValue) {
                    currentUserPicks[category.slug] = previousValue;
                } else {
                    delete currentUserPicks[category.slug];
                }

                return {
                    ...currentState,
                    [selectedUser]: currentUserPicks,
                };
            });
            setErrorMessage(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClearPick = async (category) => {
        if (isLocked || isSaving) {
            return;
        }

        const previousValue = picks[selectedUser]?.[category.slug];
        if (!previousValue) {
            return;
        }

        const nextUserPicks = { ...picks[selectedUser] };
        delete nextUserPicks[category.slug];

        setPicks({
            ...picks,
            [selectedUser]: nextUserPicks,
        });
        setIsSaving(true);
        setErrorMessage('');
        setStatusMessage('Clearing...');

        try {
            await saveOscars2026Pick({
                user_slug: selectedUser,
                user_name: selectedUser === 'matteo' ? 'Matteo' : 'Julia',
                category_slug: category.slug,
                category_name: category.name,
                category_order: OSCARS_2026_CATEGORIES.findIndex((item) => item.slug === category.slug),
                nominee_slug: '',
                updated_at: new Date().toISOString(),
            });

            setStatusMessage('Pick cleared.');
            await loadPageData();
        } catch (error) {
            setPicks((currentState) => ({
                ...currentState,
                [selectedUser]: {
                    ...currentState[selectedUser],
                    [category.slug]: previousValue,
                },
            }));
            setErrorMessage(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSetActualWinner = async (category, nomineeSlug) => {
        if (isSaving) {
            return;
        }

        const previousValue = results[category.slug];

        setResults((currentResults) => ({
            ...currentResults,
            [category.slug]: nomineeSlug,
        }));
        setIsSaving(true);
        setErrorMessage('');
        setStatusMessage('Saving winner...');

        try {
            await saveOscars2026Result({
                category_slug: category.slug,
                category_name: category.name,
                nominee_slug: nomineeSlug,
                updated_at: new Date().toISOString(),
            });

            setStatusMessage('Actual winner saved.');
            await loadPageData();
        } catch (error) {
            setResults((currentResults) => ({
                ...currentResults,
                [category.slug]: previousValue,
            }));
            setErrorMessage(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="active-dark oscars2026-page">
            <Helmet pageTitle="Oscars 2026 Predictions" disableTitleSuffix />
            {isAuthorized && !isChoosingUser && showFloatingNav ? (
                <div className="oscars2026-floating-nav">
                    <div className="oscars2026-floating-nav__count">
                        {selectedUser === 'matteo' ? 'Matteo' : 'Julia'} {completionByUser[selectedUser]}/{OSCARS_2026_CATEGORY_COUNT}
                    </div>
                    <div className="oscars2026-floating-nav__actions">
                        {selectedUserFinished ? (
                            <button type="button" onClick={scrollToComparison}>
                                Comparison
                            </button>
                        ) : null}
                        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                            Go to top
                        </button>
                    </div>
                </div>
            ) : null}
            <main className="oscars2026-shell">
                {showCelebrationModal ? (
                    <div className="oscars2026-modal">
                        <div className="oscars2026-modal__backdrop" onClick={() => setShowCelebrationModal(false)} />
                        <div className="oscars2026-modal__card" role="dialog" aria-modal="true" aria-labelledby="oscars-modal-title">
                            <p className="oscars2026-kicker">All Predictions In</p>
                            <h2 id="oscars-modal-title">You're all done!</h2>
                            <p>
                                You've predicted every category. Jump to the comparison view now.
                            </p>
                            <div className="oscars2026-modal__actions">
                                <button
                                    type="button"
                                    className="oscars2026-modal__primary"
                                    onClick={() => {
                                        setShowCelebrationModal(false);
                                        setIsComparisonOpen(true);
                                        window.setTimeout(scrollToComparison, 80);
                                    }}
                                >
                                    Go to comparison
                                </button>
                                <button
                                    type="button"
                                    className="oscars2026-modal__secondary"
                                    onClick={() => setShowCelebrationModal(false)}
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
                {!isAuthorized ? (
                    <section className="oscars2026-auth">
                        <div className="oscars2026-auth__card">
                            <p className="oscars2026-kicker">Private Page</p>
                            <h1>Oscars 2026</h1>
                            <p className="oscars2026-summary">
                                Enter the password to start your predictions.
                            </p>

                            <form className="oscars2026-auth__form" onSubmit={handleAccessSubmit}>
                                <label htmlFor="oscars-password">Password</label>
                                <input
                                    id="oscars-password"
                                    type="password"
                                    value={accessPassword}
                                    onChange={(event) => {
                                        setAccessPassword(event.target.value);
                                        if (authError) {
                                            setAuthError('');
                                        }
                                    }}
                                    autoFocus
                                />
                                <button type="submit">Enter</button>
                            </form>

                            {authError ? <div className="oscars2026-auth__error">{authError}</div> : null}
                        </div>
                    </section>
                ) : isChoosingUser ? (
                    <section className="oscars2026-auth">
                        <div className="oscars2026-auth__card">
                            <p className="oscars2026-kicker">Oscars 2026</p>
                            <h1>Who are you?</h1>
                            <p className="oscars2026-summary">
                                Choose your Oscars' winners for 2026.
                            </p>

                            <div className="oscars2026-user-picker">
                                {OSCARS_2026_USERS.map((user) => (
                                    <button key={user.slug} type="button" onClick={() => handleUserSelection(user.slug)}>
                                        {user.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>
                ) : (
                    <>
                <section className="oscars2026-hero">
                    <div className="oscars2026-hero__copy">
                        <p className="oscars2026-kicker">March 15, 2026</p>
                        <h1>Oscars 2026</h1>
                        <p className="oscars2026-summary">
                            Pick a winner in every category.
                        </p>
                        <div className="oscars2026-lockline">
                            <FiClock />
                            <span>Editing predictions locks on {ET_LOCK_LABEL}</span>
                        </div>
                    </div>

                    <div className="oscars2026-countdown">
                        <div className="oscars2026-countdown__header">
                            <span>Countdown until predictions lock</span>
                            <strong>{isLocked ? 'Locked' : 'Live'}</strong>
                        </div>
                        <div className="oscars2026-countdown__grid">
                            {countdown.map((item) => (
                                <div key={item.label} className="oscars2026-countdown__cell">
                                    <span>{formatCountdownValue(item.value)}</span>
                                    <small>{item.label}</small>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="oscars2026-toolbar">
                    <div className="oscars2026-user-switcher" aria-label="Current user">
                        <div className="oscars2026-user-meta">
                            <div className="oscars2026-user-chip is-active">
                                {selectedUser === 'matteo' ? 'Matteo' : 'Julia'}
                                <span>
                                    {completionByUser[selectedUser]}/{OSCARS_2026_CATEGORY_COUNT}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="oscars2026-switch-link"
                                onClick={() => setIsChoosingUser(true)}
                            >
                                Not {selectedUser === 'matteo' ? 'Matteo' : 'Julia'}? Switch to {otherUser.name}
                            </button>
                        </div>
                    </div>

                    <div className="oscars2026-toolbar__actions">
                        <button type="button" onClick={() => loadPageData(true)} disabled={isLoading || isSaving}>
                            <FiRefreshCw />
                            Refresh
                        </button>
                        <div className="oscars2026-status">
                            {isLoading ? 'Loading picks...' : statusMessage || ''}
                        </div>
                    </div>
                </section>

                {errorMessage ? <div className="oscars2026-banner oscars2026-banner--error">{errorMessage}</div> : null}

                <section className="oscars2026-banner oscars2026-banner--info">
                    {/* <strong>{selectedUser === 'matteo' ? 'Matteo' : 'Julia'} is editing now.</strong> */}
                    <span>
                        {isLocked
                            ? 'The ballot is locked. Picks are now read-only.'
                            : everyoneFinished
                            ? 'Predictions from both are complete. You can compare picks and keep editing until the Oscars event starts.'
                            : selectedUserFinished
                            ? `Waiting for ${otherUser.name} to complete their predictions.`
                            : 'Finish all your predictions to unlock the comparison view.'}
                    </span>
                </section>

                {selectedUserFinished ? (
                    <section className="oscars2026-comparison" ref={comparisonRef}>
                        <div className="oscars2026-comparison__header">
                            <div>
                                <p>Comparison</p>
                                <h2>
                                    {selectedUser === 'matteo' ? 'Matteo' : 'Julia'} vs {otherUser.name}
                                </h2>
                            </div>
                            <div className="oscars2026-comparison__meta">
                                <span>
                                    {selectedUser === 'matteo' ? 'Matteo' : 'Julia'} {completionByUser[selectedUser]}/{OSCARS_2026_CATEGORY_COUNT}
                                    {' · '}
                                    {otherUser.name} {completionByUser[otherUser.slug]}/{OSCARS_2026_CATEGORY_COUNT}
                                </span>
                                <button
                                    type="button"
                                    className="oscars2026-comparison__toggle"
                                    onClick={() => setIsComparisonOpen((currentValue) => !currentValue)}
                                    aria-expanded={isComparisonOpen}
                                    aria-label={isComparisonOpen ? 'Collapse comparison' : 'Expand comparison'}
                                >
                                    <FiChevronDown className={isComparisonOpen ? 'is-open' : ''} />
                                </button>
                            </div>
                        </div>

                        {isComparisonOpen ? (
                            <>
                                {!everyoneFinished ? (
                                    <div className="oscars2026-comparison__waiting">
                                        Waiting for {otherUser.name} to complete their predictions.
                                    </div>
                                ) : null}

                                <div className="oscars2026-comparison__list">
                                    {OSCARS_2026_CATEGORIES.map((category) => {
                                        const selectedNominee = category.nominees.find(
                                            (nominee) => nominee.slug === picks[selectedUser]?.[category.slug]
                                        );
                                        const otherNominee = category.nominees.find(
                                            (nominee) => nominee.slug === picks[otherUser.slug]?.[category.slug]
                                        );
                                        const actualWinner = category.nominees.find(
                                            (nominee) => nominee.slug === results[category.slug]
                                        );
                                        const isSamePick =
                                            selectedNominee &&
                                            otherNominee &&
                                            selectedNominee.slug === otherNominee.slug;

                                        return (
                                            <article
                                                key={category.slug}
                                                className={[
                                                    'oscars2026-comparison__item',
                                                    isSamePick ? 'is-match' : 'is-different',
                                                ].join(' ')}
                                            >
                                                <div className="oscars2026-comparison__top">
                                                    <h3>{category.name}</h3>
                                                    <span>
                                                        {!otherNominee
                                                            ? `${otherUser.name} still missing`
                                                            : isSamePick
                                                            ? 'Same pick'
                                                            : 'Different picks'}
                                                    </span>
                                                </div>

                                                <div className="oscars2026-comparison__picks">
                                                    <div>
                                                        <small>{selectedUser === 'matteo' ? 'Matteo' : 'Julia'}</small>
                                                        <strong>{selectedNominee ? selectedNominee.label : 'No pick'}</strong>
                                                        {selectedNominee && selectedNominee.sublabel ? (
                                                            <span>{selectedNominee.sublabel}</span>
                                                        ) : null}
                                                        <em className="oscars2026-comparison__status">
                                                            <span
                                                                className={[
                                                                    'oscars2026-status-tag',
                                                                    !actualWinner
                                                                        ? 'is-pending'
                                                                        : selectedNominee && actualWinner.slug === selectedNominee.slug
                                                                        ? 'is-correct'
                                                                        : selectedNominee
                                                                        ? 'is-incorrect'
                                                                        : 'is-empty',
                                                                ].join(' ')}
                                                            >
                                                                {!actualWinner
                                                                    ? 'Waiting for actual winner'
                                                                    : selectedNominee && actualWinner.slug === selectedNominee.slug
                                                                    ? 'Correct'
                                                                    : selectedNominee
                                                                    ? 'Incorrect'
                                                                    : 'No pick yet'}
                                                            </span>
                                                        </em>
                                                    </div>
                                                    <div>
                                                        <small>{otherUser.name}</small>
                                                        <strong>{otherNominee ? otherNominee.label : 'No pick'}</strong>
                                                        {otherNominee && otherNominee.sublabel ? (
                                                            <span>{otherNominee.sublabel}</span>
                                                        ) : null}
                                                        <em className="oscars2026-comparison__status">
                                                            <span
                                                                className={[
                                                                    'oscars2026-status-tag',
                                                                    !actualWinner
                                                                        ? 'is-pending'
                                                                        : otherNominee && actualWinner.slug === otherNominee.slug
                                                                        ? 'is-correct'
                                                                        : otherNominee
                                                                        ? 'is-incorrect'
                                                                        : 'is-empty',
                                                                ].join(' ')}
                                                            >
                                                                {!actualWinner
                                                                    ? 'Waiting for actual winner'
                                                                    : otherNominee && actualWinner.slug === otherNominee.slug
                                                                    ? 'Correct'
                                                                    : otherNominee
                                                                    ? 'Incorrect'
                                                                    : 'No pick yet'}
                                                            </span>
                                                        </em>
                                                    </div>
                                                </div>

                                                <div className="oscars2026-winner-picker">
                                                    <small>Actual winner</small>
                                                    <div className="oscars2026-winner-picker__chips">
                                                        {category.nominees.map((nominee) => (
                                                            <button
                                                                key={nominee.slug}
                                                                type="button"
                                                                className={results[category.slug] === nominee.slug ? 'is-active' : ''}
                                                                onClick={() => handleSetActualWinner(category, nominee.slug)}
                                                                disabled={isSaving}
                                                            >
                                                                {nominee.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <div className="oscars2026-winner-picker__current">
                                                        {actualWinner
                                                            ? `Winner: ${actualWinner.label}`
                                                            : 'No actual winner selected yet.'}
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            </>
                        ) : null}
                    </section>
                ) : null}

                <section className="oscars2026-board">
                    {OSCARS_2026_CATEGORIES.map((category) => {
                        const currentPick = picks[selectedUser]?.[category.slug];
                        const otherPick = otherUser ? picks[otherUser.slug]?.[category.slug] : null;

                        return (
                            <article key={category.slug} className="oscars2026-category">
                                <header className="oscars2026-category__header">
                                    <div>
                                        <p>Category</p>
                                        <h2>{category.name}</h2>
                                    </div>
                                    <div className="oscars2026-category__meta">
                                        <span>{category.nominees.length} nominees</span>
                                        {currentPick ? (
                                            <span className="oscars2026-category__picked">
                                                <FiAward />
                                                Picked
                                            </span>
                                        ) : null}
                                        <button
                                            type="button"
                                            className="oscars2026-clear"
                                            onClick={() => handleClearPick(category)}
                                            disabled={!currentPick || isLocked || isLoading || isSaving}
                                        >
                                            Clear pick
                                        </button>
                                    </div>
                                </header>

                                <div className="oscars2026-nominees">
                                    {category.nominees.map((nominee) => {
                                        const isSelected = currentPick === nominee.slug;
                                        const matchesOtherPick = everyoneFinished && otherPick === nominee.slug;

                                        return (
                                            <div
                                                key={nominee.slug}
                                                className={[
                                                    'oscars2026-nominee',
                                                    isSelected ? 'is-selected' : '',
                                                    matchesOtherPick ? 'is-other-selected' : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            >
                                                <button
                                                    type="button"
                                                    className="oscars2026-nominee__button"
                                                    onClick={() => handlePick(category, nominee.slug)}
                                                    disabled={isLocked || isLoading || isSaving}
                                                >
                                                    <span className="oscars2026-nominee__label">{nominee.label}</span>
                                                    {nominee.sublabel ? (
                                                        <span className="oscars2026-nominee__sublabel">{nominee.sublabel}</span>
                                                    ) : null}
                                                    <span className="oscars2026-nominee__radio" aria-hidden="true" />
                                                </button>

                                                <div className="oscars2026-nominee__footer">
                                                    <div className="oscars2026-nominee__footer-primary">
                                                        {nominee.trailerUrl ? (
                                                            <a href={nominee.trailerUrl} target="_blank" rel="noopener noreferrer">
                                                                YouTube trailer
                                                                <FiExternalLink />
                                                            </a>
                                                        ) : (
                                                            <span>No YouTube trailer</span>
                                                        )}
                                                    </div>

                                                    <div className="oscars2026-nominee__footer-secondary">
                                                    {everyoneFinished ? (
                                                        <small>
                                                            {otherUser.name}: {matchesOtherPick ? 'same pick' : otherPick === nominee.slug ? 'picked this' : 'did not pick'}
                                                        </small>
                                                    ) : (
                                                        <small>
                                                            {selectedUserFinished
                                                                ? `Waiting for ${otherUser.name} to complete their predictions.`
                                                                : `You'll see ${otherUser.name}'s picks once you've finished all of your predictions.`}
                                                        </small>
                                                    )}
                                                </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </article>
                        );
                    })}
                </section>
                    </>
                )}
            </main>

            <div className="backto-top">
                <ScrollToTop showUnder={160} duration={0.3}>
                    <FiChevronUp />
                </ScrollToTop>
            </div>
        </div>
    );
};

export default Oscars2026MatteoJulia;
